import { useEffect, useState } from "react";
import { string, arrayOf, shape, func } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import {
  TemplatesDataProvider,
  useActiveProjectStore,
  useStores,
  useFeatureGuardian,
  useActiveProjectsWebsocket,
} from "@hooks";
import { DASHBOARD_MODE, LOCKED_FEATURES, PROJECT_STATUS } from "@utils";
import Grid from "@material-ui/core/Grid";
import { AddOutlined } from "@material-ui/icons";
import { PageTitle, ActionButton, Alert, Tag } from "@components";
import { Column, DashboardModeSelector } from "./components";
import { NewProjectDialog } from "@dialogs";
import useStyle from "./Active.style";
import { Container } from "@material-ui/core";

export const ActiveProjectsView = observer(({
  columns,
  handleColumnNameChange,
  dashboardMode,
  onDashboardModeChange,
}) => {
  const { userStore } = useStores();
  const socket = useActiveProjectsWebsocket();
  const store = useActiveProjectStore();
  const { t } = useTranslation();
  const classes = useStyle(isMobile);

  const [ alertText, setAlertText ] = useState("");
  const [ showNewProjectModal, openNewProjectModal ] = useState(false);

  const { premiumEstimate } = userStore.subscriptionInfo;

  const { checkAccess, FeatureAlert } = useFeatureGuardian(
    LOCKED_FEATURES.ESTIMATION
  );
  
  useEffect(() => {
    socket?.joinActiveProjects();
    return () => {
      socket?.leaveActiveProjects();
    }
  }, []);

  const toNewProject = async (name) => {
    const allowed = await checkAccess();
    if ( !allowed ) return;
    
    store.setStatus(name);
    openNewProjectModal(true);
  }

  const handleNewProjectButtonClick = () => toNewProject(PROJECT_STATUS.TODO);

  return (
    <>
      <PageTitle
        title={t(dashboardMode === DASHBOARD_MODE.SHARED ? "dashboard_mode.shared" : "dashboard_mode.active")}
        titleTag={<>
          <DashboardModeSelector onChange={onDashboardModeChange} />
          <Tag
            text={`${store.projects.length}/${premiumEstimate < 0 || dashboardMode === DASHBOARD_MODE.SHARED ? "∞" : premiumEstimate}`}
            className="ml-1"
          />
        </>}
      >
        {
          dashboardMode === DASHBOARD_MODE.COMPANY &&
          <ActionButton
            icon={<AddOutlined />}
            onClick={handleNewProjectButtonClick}
          >
            {t("views.active.new_project")}
          </ActionButton>
        }
      </PageTitle>
      <Container>
        {
          dashboardMode === DASHBOARD_MODE.SHARED && store.projects?.length === 0 &&
          <Grid
            item container
            direction="column"
            alignItems="center"
            justifyContent="center"
            
            className={classes.sharedEmptyMessageContainer}
          >
            <h2 className="mb-4">
              {t("views.active.sharedEmpty.title")}
            </h2>
            <p className={classes.sharedEmptyMessageDescription}>
              {t("views.active.sharedEmpty.description")}
            </p>
          </Grid>
        }
        {
          store.projects.length > 0 &&
          <Grid
            item
            container
            spacing={4}
            justifyContent="center"
            alignItems="flex-start"
            className={isMobile ? classes.mobileColumnContainer : classes.root}
          >
            {
              columns.map(({ code, color, name }) => (
                <Column
                  key={code}
                  code={code}
                  name={name}
                  color={color}
                  onNewProjectButtonClick={toNewProject}
                  onNameChange={handleColumnNameChange}
                  isSharedList={dashboardMode === DASHBOARD_MODE.SHARED}
                />
              ))
            }
          </Grid>
        }
        {
          showNewProjectModal &&
          <TemplatesDataProvider limit={10}>
            <NewProjectDialog
              open={showNewProjectModal}
              onClose={() => openNewProjectModal(false)}
              useTemplatesDialog
              useAICreator
            />
          </TemplatesDataProvider>
        }
        <FeatureAlert />
        <Alert
          title={alertText}
          isOpen={!!alertText}
          onAccept={() => setAlertText("")}
          acceptText={t("common.close")}
        />
      </Container>
    </>
  );
});

ActiveProjectsView.propTypes = {
  handleColumnNameChange: func.isRequired,
  dashboardMode: string.isRequired,
  onDashboardModeChange: func.isRequired,
  columns: arrayOf(
    shape({
      code: string.isRequired,
      color: string.isRequired,
      name: string,
    })
  ).isRequired,
};
